import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatError, maskCPF, maskPhone } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PixInfo } from "../components/PixInfo";

export default function Adesao() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome_completo: "",
    cpf: "",
    telefone: "",
    email: "",
    data_nascimento: "",
    password: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      toast.success(`Cadastro criado! Nº ${data.user.cadastro_number}`);
      await refresh();
      nav("/painel");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="signup-page">
      <div className="grid md:grid-cols-[1fr_0.9fr] gap-10">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#166534]/15 bg-[#F0FDF4] text-[#166534] text-xs font-semibold px-3 py-1.5">
            Adesão · R$ 30,00/mês
          </span>
          <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05]">
            Preencha seu cadastro
          </h1>
          <p className="mt-4 text-neutral-700 leading-relaxed">
            Após a adesão, você receberá um número de cadastro. Faça o Pix da primeira mensalidade e
            informe o pagamento na Área do Associado. Seu cadastro será ativado após a confirmação.
          </p>

          <ul className="mt-6 space-y-2.5 text-sm text-neutral-700">
            {[
              "Sem taxas escondidas",
              "Pague por Pix ou cartão",
              "Área exclusiva para associados",
            ].map((b) => (
              <li key={b} className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#166534] mt-0.5" />
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 max-w-md">
            <PixInfo />
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border border-neutral-200 bg-white p-6 md:p-8 shadow-sm"
          data-testid="signup-form"
        >
          <div className="grid gap-4">
            <div>
              <Label htmlFor="nome">Nome completo</Label>
              <Input
                id="nome"
                data-testid="input-nome"
                value={form.nome_completo}
                onChange={(e) => set("nome_completo", e.target.value)}
                required
                minLength={3}
                className="mt-1.5"
                placeholder="Seu nome completo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  data-testid="input-cpf"
                  value={form.cpf}
                  onChange={(e) => set("cpf", maskCPF(e.target.value))}
                  required
                  className="mt-1.5"
                  placeholder="000.000.000-00"
                />
              </div>
              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  data-testid="input-telefone"
                  value={form.telefone}
                  onChange={(e) => set("telefone", maskPhone(e.target.value))}
                  required
                  className="mt-1.5"
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                data-testid="input-email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                className="mt-1.5"
                placeholder="voce@exemplo.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="data">Data de nascimento</Label>
                <Input
                  id="data"
                  type="date"
                  data-testid="input-data-nascimento"
                  value={form.data_nascimento}
                  onChange={(e) => set("data_nascimento", e.target.value)}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  data-testid="input-password"
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  required
                  minLength={6}
                  className="mt-1.5"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white h-12 text-base mt-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar adesão"}
            </Button>

            <p className="text-xs text-neutral-500 text-center leading-relaxed">
              Ao continuar, você declara estar ciente de que o Ressignificar Benefícios{" "}
              <strong>não é plano de saúde</strong> nem oferece cobertura de procedimentos.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
